import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import { DatePicker } from "antd";
import moment from "moment";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";

import { useAtom } from "jotai";
import { gbState, fYear } from "../../../components/shared/GlobalState";

import BankCardComponent from "./BankCardComponent";

const AddBalanceComponent = ({
  selectedBank,
  refreshDataFromServer,
  setShowAddBalance,
}) => {
  let navigate = useNavigate();
  const inputWidth = "230px";
  const style = {
    control: (base) => ({
      ...base,
      border: "1px solid lightgray",
      // This line disable the blue border
      boxShadow: "none",
    }),
  };

  const [{ errorLogApi, placeHolderText, user }] = useAtom(gbState);
  const [isUpdate, setIsUpdate] = useState(false);
  const [financial_year, setFinancial_year] = useState(
    user?.current_financial_year
  );
  const [data, setData] = useState();

  // Initialize with current month
  const currentMonth = dayjs().format("MMM YYYY");
  const [formData, setFormData] = useState({
    bank_id: selectedBank?._id,
    opening_balance: 0,
    closing_balance: 0,
    financial_year: financial_year,
    month: currentMonth,
  });

  //console.log("FinancialYear: ", financial_year);
  useEffect(() => {
    const getDataFromServer = () => {
      let userToken = localStorage.getItem("token");

      let urlLink = `${process.env.REACT_APP_API_URL}/bank-balance/last/${selectedBank?._id}`;

      const config = {
        method: "GET",
        url: urlLink,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      };

      axios(config)
        .then(function (response) {
          //   console.log(JSON.stringify(response.data));
          const oldData = response?.data?.bankBalances;
          console.log("oldData", oldData);
          //filter data based on financial year
          const filteredData = oldData.filter(
            (data) => data?.financial_year === user?.current_financial_year
          );
          const updatedData = filteredData.map((data) => {
            return {
              ...data,
              id: data?._id,
              month: new Date(data?.month)?.toLocaleString("default", {
                month: "long",
                year: "numeric",
              }),
              opening_balance: data?.opening_balance,
              closing_balance: data?.closing_balance,
            };
          });
          console.log(updatedData[0]);
          setData(updatedData[0]);
        })
        .catch(function (err) {
          toast.error(err?.message);
          console.log("error api call ", err?.response?.data);
        });
    };
    getDataFromServer();
  }, []);

  useEffect(() => {
    // If we have data from server, use next month from last entry
    // Otherwise, keep the current month
    console.log("data", data);

    const getNextMonth = (currentMonthStr) => {
      try {
        // Parse the current month string (e.g., "Jan 2024")
        const parsedDate = dayjs(currentMonthStr, "MMM YYYY");
        if (!parsedDate.isValid()) {
          console.error("Invalid date format:", currentMonthStr);
          return currentMonth;
        }

        // Add one month and format back
        return parsedDate.add(1, "month").format("MMM YYYY");
      } catch (error) {
        console.error("Error processing date:", error);
        return currentMonth;
      }
    };

    const month = !data?.month
      ? currentMonth
      : data?.closing_balance !== 0
      ? getNextMonth(data.month)
      : currentMonth;

    setFormData({
      ...formData,
      opening_balance:
        data?.closing_balance !== 0
          ? data?.closing_balance
          : data?.opening_balance,
      closing_balance:
        data?.closing_balance !== 0 ? 0 : data?.closing_balance ?? 0,
      month: month,
    });

    setIsUpdate(data?.closing_balance !== 0);
  }, [data]);

  const handleBalanceAdd = (e) => {
    e.preventDefault();

    if (!formData?.month) return toast.error("Please select the month");
    if (!financial_year) return toast.error("Please select a Financial Year");

    // Extract month and year from formData.month
    const [monthName, year] = formData.month.split(" ");
    const selectedYear = parseInt(year, 10);
    const financialYearRange = financial_year.split("-").map(Number);

    // Validate the selected year is within the financial year range
    if (
      selectedYear < financialYearRange[0] ||
      selectedYear > financialYearRange[1]
    ) {
      return toast.error(
        "The selected month and year do not match the Financial Year"
      );
    }

    // Validate month is within the financial year's April to March range
    const monthIndex = new Date(`${monthName} 1`).getMonth(); // Convert month name to index (0-11)
    const isMonthValid =
      (selectedYear === financialYearRange[0] && monthIndex >= 3) ||
      (selectedYear === financialYearRange[1] && monthIndex <= 2);

    if (!isMonthValid) {
      return toast.error(
        "Invalid month. Please select a month between April and March within the selected Financial Year"
      );
    }

    // Format the date to be the first day of the selected month
    const formattedDate = dayjs(`${monthName} 1, ${year}`)
      .startOf("month")
      .format("YYYY-MM-DD");

    // Proceed with balance addition
    let userToken = localStorage.getItem("token");
    const config = {
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/bank-balance/add`,
      headers: { Authorization: `Bearer ${userToken}` },
      data: {
        ...formData,
        month: formattedDate,
        financial_year: financial_year,
      },
    };

    axios(config)
      .then((response) => {
        toast.success("Balance Added Successfully.");
        // navigate("/bank/balance", { state: selectedBank });
        refreshDataFromServer();
        setShowAddBalance(false);
      })
      .catch((error) => {
        toast.error(JSON.stringify(error?.response?.data?.error));
        errorLogApi(error, "CreateBankBalance", "/bank/balance");
      });
  };

  const handleBalanceUpdate = (e) => {
    e.preventDefault();

    if (!formData?.month) return toast.error("Please select the month");
    // if (!financial_year?.value) return toast.error("Please select a Financial Year");

    // Extract month and year from formData.month
    const monthParts = formData?.month?.split(" ");
    if (!monthParts || monthParts.length !== 2) {
      return toast.error("Invalid month format");
    }

    const [monthName, year] = monthParts;
    const selectedYear = parseInt(year, 10);

    const financialYearParts = financial_year?.split("-");
    if (!financialYearParts || financialYearParts.length !== 2) {
      return toast.error("Invalid financial year format");
    }

    const financialYearRange = financialYearParts.map(Number);

    // Validate the selected year is within the financial year range
    if (
      selectedYear < financialYearRange[0] ||
      selectedYear > financialYearRange[1]
    ) {
      return toast.error(
        "The selected month and year do not match the Financial Year"
      );
    }

    // Validate month is within the financial year's April to March range
    const monthIndex = new Date(`${monthName} 1`).getMonth(); // Convert month name to index (0-11)
    const isMonthValid =
      (selectedYear === financialYearRange[0] && monthIndex >= 3) ||
      (selectedYear === financialYearRange[1] && monthIndex <= 2);

    if (!isMonthValid) {
      return toast.error(
        "Invalid month. Please select a month between April and March within the selected Financial Year"
      );
    }

    // Format the date to be the first day of the selected month
    const formattedDate = dayjs(`${monthName} 1, ${year}`)
      .startOf("month")
      .format("YYYY-MM-DD");

    // Proceed with balance update
    let userToken = localStorage.getItem("token");
    const config = {
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/bank-balance/update/${data?._id}`,
      headers: { Authorization: `Bearer ${userToken}` },
      data: {
        ...formData,
        financial_year: financial_year?.value,
        month: formattedDate,
      },
    };

    axios(config)
      .then((response) => {
        toast.success("Balance Added Successfully.");
        // navigate("/bank/balance", { state: selectedRecord?.bank_id });
        refreshDataFromServer();
        setShowAddBalance(false);
      })
      .catch((error) => {
        toast.error(JSON.stringify(error?.response?.data?.error));
        errorLogApi(error, "CreateBankBalance", "/bank/balance");
      });
  };

  const handleChange = ({ currentTarget: input }) => {
    setFormData({
      ...formData,
      [input.name]: input.value,
    });
  };

  return (
    <div className="flex justify-center w-full">
      <div className="w-[600px] max-w-full bg-white rounded-xl shadow-2xl border border-gray-100">
        <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Bank Balance</h2>
              <p className="mt-1 text-sm text-gray-600">
                {selectedBank?.bank_name || "Bank"} -{" "}
                {selectedBank?.account_number || "Account"}
              </p>
            </div>
            <button
              className="px-4 py-2 text-sm font-medium text-gray-600 transition-colors duration-200 bg-white rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300"
              onClick={(e) => {
                e.preventDefault();
                setShowAddBalance(false);
              }}
            >
              Back
            </button>
          </div>
        </div>

        <form autoComplete="off" className="p-6 space-y-6">
          <div className="space-y-5">
            <div className="p-4 rounded-lg bg-gray-50">
              <label className="block mb-2 text-sm font-semibold text-gray-700">
                Month
              </label>
              <DatePicker
                className="w-full h-[42px] border border-gray-200 rounded-lg shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 bg-white"
                name="month"
                onChange={(date, dateString) => {
                  setFormData({
                    ...formData,
                    month: dateString,
                  });
                }}
                showToday={true}
                picker="month"
                placeholder="Select Month"
                format="MMM YYYY"
                value={
                  formData.month ? dayjs(formData.month, "MMM YYYY") : null
                }
              />
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div className="p-4 rounded-lg bg-gray-50">
                <label className="block mb-2 text-sm font-semibold text-gray-700">
                  Opening Balance
                </label>
                <div className="relative">
                  <span className="absolute text-gray-500 -translate-y-1/2 left-3 top-1/2">
                    $
                  </span>
                  <input
                    className="w-full p-3 pl-8 text-sm bg-white border border-gray-200 rounded-lg shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    name="opening_balance"
                    type="number"
                    min="0"
                    required
                    placeholder="0.00"
                    autoComplete="off"
                    onChange={handleChange}
                    value={formData.opening_balance}
                  />
                </div>
              </div>

              <div className="p-4 rounded-lg bg-gray-50">
                <label className="block mb-2 text-sm font-semibold text-gray-700">
                  Closing Balance
                </label>
                <div className="relative">
                  <span className="absolute text-gray-500 -translate-y-1/2 left-3 top-1/2">
                    $
                  </span>
                  <input
                    className="w-full p-3 pl-8 text-sm bg-white border border-gray-200 rounded-lg shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    name="closing_balance"
                    type="number"
                    min="0"
                    placeholder="0.00"
                    autoComplete="off"
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      if (Number.isInteger(value) && value >= 0) {
                        handleChange(e);
                      } else {
                        e.preventDefault();
                      }
                    }}
                    value={formData.closing_balance}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-gray-100">
            {isUpdate ? (
              <button
                className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 shadow-sm"
                onClick={(e) => {
                  e.preventDefault();
                  handleBalanceAdd(e);
                }}
              >
                Create Balance
              </button>
            ) : (
              <button
                className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 shadow-sm"
                onClick={(e) => {
                  e.preventDefault();
                  handleBalanceUpdate(e);
                }}
              >
                Update Balance
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddBalanceComponent;
